import {
  useCreateUserMutation,
  usePasswordResetMutation,
  useUpdateUserMutation,
} from '@/api/hooks/useUsers'
import type { CreateUserPayload } from '@/api/hooks/useUsers/transformers'
import { User } from '@/api/types'
import { useAuth } from '@/contexts/AuthContext'
import { GridApi, GridReadyEvent } from 'ag-grid-community'
import { Form, Modal } from 'antd'
import { useCallback, useRef, useState } from 'react'
import { Role } from '../types'

type Domain = CreateUserPayload['domains'][number]

export type CreateUserForm = Pick<
  CreateUserPayload,
  'email' | 'name' | 'password' | 'source'
> & {
  access_type: 'gravitate' | 'tenant'
  selected_tenant: string
  selected_role_id: Role['_id']
}

export const useUserCRUD = (onAfterMutate?: () => void) => {
  const [isCreateDrawerOpen, setIsCreateDrawerOpen] = useState(false)
  const [form] = Form.useForm<CreateUserForm>()

  const createMutation = useCreateUserMutation()
  const updateMutation = useUpdateUserMutation()
  const resetPasswordMutation = usePasswordResetMutation()
  const { user: currentUser } = useAuth()

  const openCreateDrawer = useCallback(() => {
    form.resetFields()
    form.setFieldsValue({
      access_type: 'tenant',
      selected_tenant: undefined,
      selected_role_id: undefined,
      source: 'Basic',
    })
    setIsCreateDrawerOpen(true)
  }, [form])

  const closeCreateDrawer = useCallback(() => {
    setIsCreateDrawerOpen(false)
    form.resetFields()
    form.setFieldsValue({
      access_type: 'tenant',
      selected_tenant: undefined,
      selected_role_id: undefined,
      source: 'Basic',
    })
  }, [form])

  // creating users
  const handleCreateSubmit = useCallback(() => {
    form.validateFields().then((values) => {
      const { access_type, selected_tenant, selected_role_id, ...restValues } =
        values

      const selectedTenantNames: string[] =
        access_type === 'gravitate'
          ? ['Gravitate']
          : selected_tenant
            ? [selected_tenant]
            : []

      if (selectedTenantNames.length === 0) {
        form.setFields([
          {
            name: 'selected_tenant',
            errors: ['Select a tenant'],
          },
        ])
        return
      }

      const domainsPayload = selectedTenantNames.map((tenantName: string) => {
        return {
          tenant_name: tenantName,
          granted_by: currentUser?.email || 'system',
          roles: [selected_role_id],
          granted_on: new Date().toISOString(),
        } as Domain
      })

      const payload: CreateUserPayload = {
        ...restValues,
        email_confirmed: false,
        is_active: true,
        api_keys: [],
        domains: domainsPayload,
      }

      createMutation.mutate(
        {
          body: payload,
          params: {
            // TODO: Eventually, customers will be able to manage their own users, but for now it's assumed that only gravitate admins are managing users
            header: {
              'X-Tenant-Name': 'Gravitate',
            },
          },
        },
        {
          onSuccess: () => {
            closeCreateDrawer()
            onAfterMutate?.()
          },
        },
      )
    })
  }, [
    form,
    updateMutation,
    createMutation,
    closeCreateDrawer,
    currentUser,
    onAfterMutate,
  ])

  const handleToggleStatus = useCallback(
    (user: User) => {
      const setActive = (is_active: boolean) => {
        updateMutation.mutate(
          {
            body: { ...user, is_active },
            params: { header: { 'X-Tenant-Name': 'Gravitate' } },
          },
          {
            onSuccess: () => {
              onAfterMutate?.()
            },
          },
        )
      }
      if (user.is_active) {
        Modal.confirm({
          title: 'Deactivate User',
          content: `Are you sure you want to deactivate ${user.name}?`,
          okText: 'Deactivate',
          okType: 'danger',
          onOk: () => setActive(false),
        })
      } else {
        setActive(true)
      }
    },
    [onAfterMutate, updateMutation],
  )

  return {
    isCreateDrawerOpen,
    form,
    createMutation,
    updateMutation,
    resetPasswordMutation,
    openCreateDrawer,
    closeCreateDrawer,
    handleCreateSubmit,
    handleToggleStatus,
  }
}

export const useGridManagement = () => {
  const [selectedUsers, setSelectedUsers] = useState<User[]>([])
  const gridRef = useRef<GridApi | null>(null)

  const handleGridReady = useCallback((params: GridReadyEvent<User>) => {
    gridRef.current = params.api
  }, [])

  const handleSelectionChanged = useCallback(() => {
    const selectedRows = gridRef.current?.getSelectedRows() || []
    setSelectedUsers(selectedRows)
  }, [])

  return {
    selectedUsers,
    gridRef,
    handleGridReady,
    handleSelectionChanged,
  }
}
